import './index.css'
import React, { useEffect, useState } from 'react'
import Layout from '../components/Layout'
import Helmet from 'react-helmet'

const URL = 'https://el-habib.me'

const SkillBadge = ({ skill, level, delay = 0 }) => (
  <div className="glass-card skill-badge animate-on-scroll" style={{ animationDelay: `${delay}ms` }}>
    <span className="skill-name">{skill}</span>
    <div className="skill-level">
      <div className="skill-progress" style={{ width: level }}></div>
    </div>
  </div>
)

const ExperienceCard = ({ company, position, period, location, achievements, technologies, delay = 0 }) => (
  <div className="glass-card experience-card animate-on-scroll" style={{ animationDelay: `${delay}ms` }}>
    <div className="experience-header">
      <h3 className="experience-position">{position}</h3>
      <div className="experience-company">{company}</div>
      <div className="experience-meta">
        <span className="experience-period">{period}</span>
        <span className="experience-location">{location}</span>
      </div>
    </div>
    {technologies && technologies.length > 0 && (
      <div className="experience-technologies">
        <h4 className="technologies-title">Technologies & Tools:</h4>
        <div className="technologies-grid">
          {technologies.map((tech, index) => (
            <span key={index} className="tech-badge">{tech}</span>
          ))}
        </div>
      </div>
    )}
  </div>
)

const ProjectCard = ({ title, description, technologies, github, demo, featured, delay = 0 }) => (
  <div className={`glass-card project-card animate-on-scroll ${featured ? 'featured' : ''}`} style={{ animationDelay: `${delay}ms` }}>
    <div className="project-header">
      <h3 className="project-title">{title}</h3>
      <div className="project-links">
        {github && (
          <a href={github} target="_blank" rel="noopener noreferrer" className="project-link">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
            </svg>
          </a>
        )}
        {demo && (
          <a href={demo} target="_blank" rel="noopener noreferrer" className="project-link">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z" />
            </svg>
          </a>
        )}
      </div>
    </div>
    <p className="project-description">{description}</p>
    <div className="project-technologies">
      {technologies.map((tech, index) => (
        <span key={index} className="tech-tag">{tech}</span>
      ))}
    </div>
  </div>
)

const CertificationCard = ({ title, issuer, link, delay = 0, }) => (
  <a href={link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
    <div className="glass-card certification-card animate-on-scroll" style={{ animationDelay: `${delay}ms`, height: '200px' }}>
      <h3 className="certification-title">{title}</h3>
      <p className="certification-issuer">{issuer}</p>
    </div>
  </a>
)

const FloatingParticles = () => (
  <div className="floating-particles">
    {[...Array(8)].map((_, i) => (
      <div key={i} className="particle"></div>
    ))}
  </div>
)

const LoadingOverlay = ({ isLoading }) => {
  if (!isLoading) return null;

  return (
    <div className="loading-overlay">
      <div className="loading-spinner"></div>
    </div>
  );
}

const IndexPage = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Intersection Observer for scroll animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animated');
          }
        });
      },
      { threshold: 0.1 }
    );

    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [isLoading]);

  const myImage = {
    src: "/img/photo.png"
  }

  const skills = [
    { skill: "Golang ♥", level: "99%" },
    { skill: "TypeScript/JavaScript", level: "90%" },
    { skill: "Python", level: "85%" },
    { skill: "Java", level: "80%" },
    { skill: "React", level: "97%" },
    { skill: "React Native", level: "90%" },
    { skill: "Docker/Kubernetes", level: "85%" },
    { skill: "AWS", level: "80%" },
    { skill: "GCP", level: "80%" },
    { skill: "PostgreSQL", level: "85%" },
    { skill: "Redis", level: "80%" },
    { skill: "Terraform", level: "75%" }
  ]

  const experiences = [
    {
      company: "-",
      position: "Senior Software Engineer",
      period: "Jan 2024 — Present",
      location: "Casablanca, Morocco",
      technologies: []
    },
    {
      company: "Avito.ma",
      position: "Backend Engineer",
      period: "June 2020 — Jan 2024",
      location: "Casablanca, Morocco",
      technologies: ["Go", "Java", "Scala", "PHP", "Docker", "Kubernetes", "Terraform", "AWS", "Microservices"]
    },
    {
      company: "Nimble Ways",
      position: "Full Stack Developer",
      period: "August 2019 — June 2020",
      location: "Casablanca, Morocco",
      technologies: ["JavaScript", "ReactJS", "Redux", "Spring Boot", "Java", "Maven", "Git"]
    }
  ]

  const internships = [
    {
      company: "Société Générale Africa Technologies & Services",
      position: "Frontend Developer Intern",
      period: "February 2019 — June 2019",
      location: "Casablanca, Morocco",
      technologies: ["TypeScript", "React", "ANTLR", "Monaco-Editor"]
    },
    {
      company: "CNRST",
      position: "Full Stack Developer Intern",
      period: "July 2018 — August 2018",
      location: "Rabat, Morocco",
      technologies: ["Java", "Spring", "Thymeleaf", "JasperReport"]
    }
  ]

  const projects = [
    {
      title: "Zscaler Cloud SDK & Terraform Providers",
      description: "Comprehensive SDK and Terraform providers for Zscaler cloud security platform, enabling infrastructure as code management for enterprise security solutions.",
      technologies: ["Golang", "Terraform", "Cloud Security", "SDK", "API"],
      github: "https://github.com/zscaler/zscaler-sdk-go",
      featured: true
    },
    {
      title: "Zscaler Terraformer",
      description: "CLI tool to generate Terraform configuration files from existing Zscaler cloud resources, simplifying infrastructure migration and management.",
      technologies: ["Golang", "Terraform", "CLI", "Cloud", "Migration"],
      github: "https://github.com/zscaler/zscaler-terraformer",
      featured: true
    },
    {
      title: "form-go",
      description: "Golang package for effortless struct-to-URL-form encoding with tag-based field selection supporting all primitive types.",
      technologies: ["Golang", "Package", "Forms", "Encoding"],
      github: "https://github.com/amazzalel-habib/form-go",
      featured: false
    },
    {
      title: "use-paginated",
      description: "React hook library providing helpful pagination logic implementation for React applications with TypeScript support.",
      technologies: ["TypeScript", "React", "Hook", "NPM", "Library"],
      github: "https://github.com/amazzalel-habib/use-paginated",
      featured: false
    },
    {
      title: "TodoLangEditor",
      description: "Custom web editor built with TypeScript, React, ANTLR and Monaco-Editor for a custom scripting language with syntax highlighting.",
      technologies: ["TypeScript", "React", "ANTLR", "Monaco-Editor", "Language"],
      github: "https://github.com/amazzalel-habib/TodoLangEditor",
      featured: false
    }
  ]

  const certifications = [
    {
      "link": "https://www.coursera.org/account/accomplishments/certificate/MMMWWXHVTLUD",
      "title": "AWS Fundamentals: Going Cloud-Native",
      "issuer": "An online non-credit course authorized by Amazon Web Services and offered through Coursera."
    },
    {
      "link": "https://www.coursera.org/account/accomplishments/certificate/BSDWBJNQV4YN",
      "title": "AWS Fundamentals: Addressing Security Risk",
      "issuer": "An online non-credit course authorized by Amazon Web Services and offered through Coursera."
    },
    {
      "link": "https://courses.cognitiveclass.ai/certificates/e75731ddc1804855aacc3642d9a046d8",
      "title": "Docker Essentials: A Developer Introduction",
      "issuer": "Provided by IBM Developer Skills Network."
    },
    {
      "link": "https://www.coursera.org/account/accomplishments/certificate/9FZQDDTYF5B8",
      "title": "Machine Learning",
      "issuer": "An online non-credit course authorized by Stanford University and offered through Coursera."
    },
    {
      "link": "https://www.cloudskillsboost.google/public_profiles/1eca97d5-9c02-49cd-ab80-e96e18543026",
      "title": "Google Cloud Platform",
      "issuer": "Google Cloud Skills Boost"
    }
  ]

  return (
    <Layout>
      <LoadingOverlay isLoading={isLoading} />
      <FloatingParticles />

      <Helmet>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
        <meta property="og:image" content={`${URL}${myImage.src}`} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Person',
            name: 'EL-HABIB AMAZZAL',
            jobTitle: 'Senior Software Engineer',
            image: {
              '@type': 'ImageObject',
              url: `${URL}${myImage.src}`,
              width: 300,
              height: 300,
            },
            url: URL,
            sameAs: [
              'https://github.com/amazzalel-habib',
              'https://www.linkedin.com/in/amazzal/',
            ],
          }, null, 2)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-image">
            <img src={myImage.src} alt="EL-HABIB AMAZZAL" className="profile-photo" />
          </div>
          <div className="hero-text">
            <h1 className="hero-name">EL-HABIB AMAZZAL</h1>
            <p className="hero-title">Senior Software Engineer</p>
            <p className="hero-description">
              I'm a software engineer passionate about problem-solving. I enjoy learning and easily grasp new concepts and technologies. I also have a strong fondness for writing clean code and building scalable, maintainable, and high-performing services. Detail-oriented and committed to excellence.
            </p>
            <div className="hero-links">
              <a href="https://github.com/amazzalel-habib" target="_blank" rel="noopener noreferrer" className="glass-button">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
                </svg>
                GitHub
              </a>
              <a href="/contact" className="glass-button">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
                Contact
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section 
      <section className="section">
        <div className="section-header">
          <h2 className="section-title">Professional Experience</h2>
        </div>
        <div className="experience-timeline">
          {experiences.map((exp, index) => (
            <ExperienceCard key={index} {...exp} delay={index * 200} />
          ))}
        </div>
      </section>
*/}
      {/* Internships Section 
      <section className="section">
        <div className="section-header">
          <h2 className="section-title">Internships</h2>
        </div>
        <div className="experience-timeline">
          {internships.map((internship, index) => (
            <ExperienceCard key={index} {...internship} delay={index * 200} />
          ))}
        </div>
      </section>
*/}
      {/* Skills Section */}
      <section className="section">
        <div className="section-header">
          <h2 className="section-title">Technical Skills</h2>
        </div>
        <div className="skills-grid">
          {skills.map((skill, index) => (
            <SkillBadge key={index} {...skill} delay={index * 100} />
          ))}
        </div>
      </section>

      {/* Projects Section */}
      <section className="section">
        <div className="section-header">
          <h2 className="section-title">Featured Projects</h2>
        </div>
        <div className="projects-grid">
          {projects.map((project, index) => (
            <ProjectCard key={index} {...project} delay={index * 150} />
          ))}
        </div>
      </section>

      {/* Certifications Section */}
      <section className="section">
        <div className="section-header">
          <h2 className="section-title">Certifications</h2>
        </div>
        <div className="skills-grid">
          {certifications.map((cert, index) => (
            <CertificationCard key={index} {...cert} delay={index * 100} />
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section className="section">
        <div className="section-header">
          <h2 className="section-title">Let's Connect</h2>
        </div>
        <div className="contact-content">
          <p className="contact-text">
            I'm always open to discussing new opportunities, interesting projects, or just having a chat about technology and software engineering.
          </p>
          <div className="contact-links">
            <a href="/contact" className="glass-button">
              Get In Touch
            </a>
            <a href="https://calendly.com/amazzal" target="_blank" rel="noopener noreferrer" className="glass-button">
              Schedule a Meeting
            </a>
            <a href="https://www.linkedin.com/in/amazzal/" target="_blank" rel="noopener noreferrer" className="glass-button">
              LinkedIn
            </a>
          </div>
        </div>
      </section>
    </Layout>
  )
}

export default IndexPage